import { hasOwnerLikeRole, isAdmin } from "@omnia/shared/session/utils"
import { showEditDialog as showHoldingPermissionsEditDialog } from "../../../shared_esm"
import { getTestSettings } from "../../session/settings/test"
import { IOmniaShell } from "../../shell"
import { RoleId } from "../../user"
import { defineDirective } from "../../utils/angular"

/** @internal */
export class UserMenuCtrl {
    static create(): ng.IDirective {
        return defineDirective(UserMenuCtrl, "shared/user/menu/menu.html")
    }

    static $inject = ["OmniaShell"]

    readonly debug = this.session.debugMode
    readonly admin = this.session.currentUserRole <= RoleId.Admin
    readonly prod = this.session.production

    readonly testSettings = getTestSettings(this.session)

    constructor(
        private shell: IOmniaShell,
        private session = shell.getSession(),
        readonly canEditPermissions = hasOwnerLikeRole(session),
        readonly showUserPermissionsMenu = session.config.holdingUserPermissions || isAdmin(session),
        readonly canEditConnections = session.permissions.currentHolding.write
    ) {}

    get currentUser() {
        return this.session.currentUser
    }

    get currentUserInitials(): string {
        return (
            (this.currentUser.FirstName.length > 0 ? this.currentUser.FirstName[0] : "") +
            (this.currentUser.LastName.length > 0 ? this.currentUser.LastName[0] : "")
        )
    }

    get currentHolding() {
        return this.session.holding
    }

    get currentCroppingYear() {
        return this.session.cropYear
    }

    get disabled(): boolean {
        return this.shell.disableMenu
    }

    manageUser() {
        this.showDialog("omnia-user-edit")
    }

    changePassword() {
        this.showDialog("omnia-change-password")
    }

    editPermissions() {
        showHoldingPermissionsEditDialog(this.shell)
    }

    logout() {
        this.session.logout()
    }

    private showDialog(template: string) {
        this.shell.showTemplateAsDialog("<" + template + "/>", undefined, { backdrop: "static" })
    }

    // --------------------------------------------------
    // Manage Connections
    // --------------------------------------------------

    editConnections() {
        this.showDialog("omnia-apps-manage")
    }

    // --------------------------------------------------
    // Manage Holdings
    // --------------------------------------------------

    get canManageHoldings() {
        return !this.session.isReadOnly((p) => p.holdings)
    }

    manageHoldings() {
        if (!this.canManageHoldings) return

        this.showDialog("omnia-user-holdings")
    }

    // --------------------------------------------------
    // Manage Partners
    // --------------------------------------------------

    get canManagePartners() {
        return this.session.permissions.currentHolding.write
    }

    openManagePartnersDialog() {
        if (!this.canManagePartners) return

        this.shell.showComponentAsDialog("omnia-manage-partners-dialog", null, {
            size: "md",
        })
    }

    // --------------------------------------------------
    // Toggle Show IDs
    // --------------------------------------------------

    get showIds() {
        return this.shell.showIds
    }

    get canToggleShowIds() {
        return this.debug
    }

    toggleShowIds(event: JQuery.Event) {
        event?.stopPropagation()

        if (!this.canToggleShowIds) return

        this.shell.showIds = !this.showIds
    }

    // --------------------------------------------------
    // Toggle Auto-Refresh
    // --------------------------------------------------

    get autoRefresh() {
        return this.shell.enableAutoRefresh
    }

    get canToggleAutoRefresh() {
        return this.debug
    }

    toggleAutoRefresh(event: JQuery.Event) {
        event?.stopPropagation()

        if (!this.canToggleAutoRefresh) return

        const { shell } = this
        shell.enableAutoRefresh = !shell.enableAutoRefresh
    }

    // --------------------------------------------------
    // Toggle Refresh Button
    // --------------------------------------------------

    get showRefreshButton() {
        return this.testSettings.showRefreshButton
    }

    get canToggleRefreshButton() {
        return this.admin
    }

    toggleRefreshButton(event: JQuery.Event) {
        event.stopPropagation()

        if (!this.canToggleRefreshButton) return

        return this.testSettings.toggleRefreshButton()
    }
}
